// ============ i18n — ไทย / อังกฤษ ============
// Lightweight, dependency-free. Strings are translated inline at the call site
// with t('ไทย', 'English') so there's no separate key registry to keep in sync.
// Language persists in localStorage and sets <html lang>. Adding a 3rd language
// later means widening Lang + the t() signature.

import React, { createContext, useContext, useEffect, useState } from 'react';

export type Lang = 'th' | 'en';

interface LangState { lang: Lang; setLang: (l: Lang) => void; }
const LangCtx = createContext<LangState>({ lang: 'th', setLang: () => {} });

export function LangProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const saved = (typeof localStorage !== 'undefined' && localStorage.getItem('mm_lang')) as Lang | null;
    return saved === 'en' || saved === 'th' ? saved : 'th';
  });
  const setLang = (l: Lang) => {
    setLangState(l);
    try { localStorage.setItem('mm_lang', l); } catch { /* ignore */ }
    if (typeof document !== 'undefined') document.documentElement.lang = l;
  };
  useEffect(() => { if (typeof document !== 'undefined') document.documentElement.lang = lang; }, [lang]);
  return <LangCtx.Provider value={{ lang, setLang }}>{children}</LangCtx.Provider>;
}

export function useLang() { return useContext(LangCtx); }

/** Hook returning an inline translator: const t = useT(); t('ไทย', 'English'). */
export function useT() {
  const { lang } = useLang();
  return (th: string, en: string) => (lang === 'en' ? en : th);
}

/** Pick a value by language (for non-string data, e.g. arrays). */
export function useTL() {
  const { lang } = useLang();
  return <T,>(th: T, en: T): T => (lang === 'en' ? en : th);
}
